import * as React from 'react';
import { Input, Toggle } from '@teamsnap/snap-ui';
import { NgbIntegration } from '../hooks/useApiGetNgbIntegrations';
import { Controller } from 'react-hook-form';
import { gql, useMutation } from '@apollo/client';
import { reactiveFeedbackMessage } from 'core/apollo-cache';
import {
  ReactHookFormInputs,
  useRegistrationContext,
} from '../RegistrationContext';
import * as GetForm from 'core/gql/queries/show-form';
import { RegistrationSteps } from '../types';
import { format, parseISO } from 'date-fns';

type NgbIntegrationStepProps = {
  ngbIntegrationsRawData: {
    ngbIntegrations: NgbIntegration[];
  };
};

export default function NgbIntegrationStep(props: NgbIntegrationStepProps) {
  const { ngbIntegrationsRawData } = props;
  const {
    orgId,
    formId,
    savedFormData,
    isLoadingSavedFormData,
    reactHookForm,
    currentStepSubmitFunction,
    triggerChangeStep,
    closeStepper,
  } = useRegistrationContext();
  const enabled = reactHookForm.watch('enabled');

  const [createOrUpdateFormNgbIntegration] = useMutation(
    CREATE_OR_UPDATE_FORM_NGB_INTEGRATION_MUTATION,
    {
      refetchQueries: [
        {
          query: GetForm.QUERY,
          variables: { formId: formId, organizationId: Number(orgId) },
        },
      ],
    }
  );

  const ngbIntegrationData = (ngbIntegrationsRawData?.ngbIntegrations || [])[0];
  const savedNgbIntegrationData = React.useMemo(() => {
    if (!savedFormData || !ngbIntegrationData) {
      return null;
    }
    return savedFormData.ngbIntegrations.find(
      (ngbIntegration) =>
        ngbIntegration.ngbIntegrationId === ngbIntegrationData.id
    );
  }, [ngbIntegrationData, savedFormData]);

  currentStepSubmitFunction.current = (handler) => async (formData) => {
    const variables = {
      formId,
      ngbIntegrations: {
        ngbIntegrationId: ngbIntegrationData.id,
        ...parseFormDataPerNgbCode(
          ngbIntegrationData.code,
          formData,
          savedNgbIntegrationData
        ),
      },
    };
    await performMutation(createOrUpdateFormNgbIntegration, variables);
    handler === 'handleNextStep' &&
      triggerChangeStep(RegistrationSteps.GROUP_NAMES);
    handler === 'handleBackStep' &&
      triggerChangeStep(RegistrationSteps.SCHEDULE);
    handler === 'handleSaveAndExitStepper' && closeStepper();
  };

  const savedNgbIntegrationDataForInputs = React.useMemo(() => {
    if (!ngbIntegrationData || !savedNgbIntegrationData) {
      return null;
    }
    switch (ngbIntegrationData.code) {
      case 'usa_lacrosse': {
        return {
          enabled: savedNgbIntegrationData.enabled,
          season_dates: humanizeIsoDate(
            savedNgbIntegrationData.season_end_date
          ),
        };
      }
      case 'usa_hockey': {
        const seasonDatesFieldData = (
          ngbIntegrationData.metadata?.formRequirements?.fields || []
        ).find((field) => field.name === 'season_dates');
        return {
          enabled: savedNgbIntegrationData.enabled,
          season_dates:
            savedNgbIntegrationData.season_start_date &&
            savedNgbIntegrationData.season_end_date
              ? [
                  humanizeIsoDate(savedNgbIntegrationData.season_start_date),
                  humanizeIsoDate(savedNgbIntegrationData.season_end_date),
                ].join('|')
              : parseOptionsPerNgbFieldName(
                  'season_dates',
                  ngbIntegrationData.code,
                  seasonDatesFieldData?.options
                )[0].value ?? null,
        };
      }
      case 'hockey_canada': {
        const seasonDatesFieldData = (
          ngbIntegrationData.metadata?.formRequirements?.fields || []
        ).find((field) => field.name === 'season_dates');
        return {
          enabled: savedNgbIntegrationData.enabled,
          season_dates: savedNgbIntegrationData.season_start_date &&
          savedNgbIntegrationData.season_end_date
            ? [
                humanizeIsoDate(savedNgbIntegrationData.season_start_date),
                humanizeIsoDate(savedNgbIntegrationData.season_end_date),
              ].join('|') : parseOptionsPerNgbFieldName(
            'season_dates',
            ngbIntegrationData.code,
            seasonDatesFieldData?.options
          )[1].value ?? null,
        };
      }

      default:
        return {
          enabled: savedNgbIntegrationData.enabled,
        };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedNgbIntegrationData, ngbIntegrationData, savedFormData]);

  // TODO: This useEffects can be deleted once we upgrade to react-hook-form v7
  React.useEffect(() => {
    reactHookForm.register('enabled');
    return () => {
      reactHookForm.unregister('enabled');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    if (!isLoadingSavedFormData && savedNgbIntegrationDataForInputs) {
      // TODO: Check if we can use just reactHookForm.reset with react-hook-form v7
      reactHookForm.setValue(
        'enabled',
        savedNgbIntegrationDataForInputs.enabled
      );
      reactHookForm.setValue(
        'season_dates',
        savedNgbIntegrationDataForInputs.season_dates
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedNgbIntegrationDataForInputs, isLoadingSavedFormData]);

  if (!ngbIntegrationData || isLoadingSavedFormData) {
    return null;
  }

  return (
    <form className="sui-rounded sui-border sui-border-solid sui-border-neutral-border sui-p-4">
      <div className="sui-flex sui-items-center sui-gap-2">
        <figure className="sui-h-[64px]">
          <img
            src={LOGO_PER_NGB[ngbIntegrationData.code]}
            alt="Logo"
            className="sui-w-full sui-h-full"
          />
        </figure>
        <p className="sui-flex-1 sui-heading-sm">
          {ngbIntegrationData.metadata.formRequirements?.toggleLabel}
        </p>
        <Toggle
          name="enabled"
          on={enabled}
          onClick={() => reactHookForm.setValue('enabled', !enabled)}
        />
      </div>
      {enabled && (
        <div className="sui-grid sui-gap-2 sui-mt-4">
          {ngbIntegrationData.metadata.formRequirements?.fields.map((field) => (
            // NOTE: If inputs get more complex this area will deserve its own component
            <Controller
              key={`ngb-integration-field-${field.name}`}
              name={field.name}
              defaultValue={
                savedNgbIntegrationDataForInputs?.[field.name] || ''
              }
              control={reactHookForm.control}
              rules={{
                required: field.required ? 'This field is required' : undefined,
              }}
              as={
                <Input
                  type={field.type}
                  name={field.name}
                  label={field.label}
                  helpText={field.adminHelpText}
                  disabled={savedFormData?.hasSubmittedFormResults && ngbIntegrationData.code === 'hockey_canada'}
                  required={field.required}
                  errorState={Boolean(reactHookForm.errors[field.name])}
                  options={parseOptionsPerNgbFieldName(
                    field.name,
                    ngbIntegrationData.code,
                    field.options
                  )}
                  inputProps={{
                    ...(field.type === 'date' && {
                      min: format(new Date(), 'yyyy-MM-dd'),
                      max: format(
                        new Date(
                          new Date().setFullYear(new Date().getFullYear() + 5)
                        ),
                        'yyyy-MM-dd'
                      ),
                    }),
                  }}
                />
              }
            />
          ))}
        </div>
      )}
    </form>
  );
}

async function performMutation(
  functionToCall: (options: { variables: MutationPayload }) => Promise<any>,
  variables: MutationPayload
) {
  try {
    const response = await functionToCall({ variables });
    return response;
  } catch (error) {
    let message =
      'Oh no! It looks like something went wrong. Please try again or contact TeamSnap Support for help.';
    if (error.graphQLErrors) {
      const [errors] = error.graphQLErrors;
      message = errors.message || message;
    }
    reactiveFeedbackMessage({
      message,
      isError: true,
      autoclose: true,
    });
    throw error;
  }
}

function parseFormDataPerNgbCode(
  code: NgbIntegration['code'],
  formData: ReactHookFormInputs,
  // NOTE: We are sending the savedNgbIntegrationData to keep the season dates if the user disables the integration
  savedNgbIntegrationData: GetForm.ShowRegistrationFormResponse['showRegistrationForm']['ngbIntegrations'][0]
) {
  switch (code) {
    case 'usa_lacrosse':
      return {
        enabled: formData.enabled,
        seasonEndDate: formData.season_dates
          ? new Date(formData.season_dates).toISOString()
          : savedNgbIntegrationData?.season_end_date || '',
      };
    case 'usa_hockey': {
      const [seasonStartDate, seasonEndDate] = formData.season_dates
        ? formData.season_dates.split('|')
        : [];
      return {
        enabled: formData.enabled,
        seasonStartDate: seasonStartDate
          ? new Date(seasonStartDate).toISOString()
          : savedNgbIntegrationData?.season_start_date || '',
        seasonEndDate: seasonEndDate
          ? new Date(seasonEndDate).toISOString()
          : savedNgbIntegrationData?.season_end_date || '',
      };
    }
    case 'hockey_canada': {
      const [seasonStartDate, seasonEndDate] = formData.season_dates
      ? formData.season_dates.split('|')
      : [];
      return {
        enabled: formData.enabled,
        seasonStartDate: seasonStartDate
          ? new Date(seasonStartDate).toISOString()
          : savedNgbIntegrationData?.season_start_date || '',
        seasonEndDate: seasonEndDate
          ? new Date(seasonEndDate).toISOString()
          : savedNgbIntegrationData?.season_end_date || '',
      };
    }
    default:
      return formData;
  }
}

function parseOptionsPerNgbFieldName(
  fieldName: keyof ReactHookFormInputs,
  code: string,
  options?: any[]
): { label: string; value: string }[] {
  switch (fieldName) {
    case 'season_dates':
      return options?.map((option) => {
        if(code === 'hockey_canada') {
          const startDateToSet = parseISO(option.seasonStartDate);
          const endDateToSet = parseISO(option.seasonEndDate);
          return {
            label: `${format(startDateToSet, 'yyyy')} - ${format(
              endDateToSet,
              'yyyy'
            )}`,
            value: [option.seasonStartDate, option.seasonEndDate].join('|'),
          };
        }
        else {
          // parseISO will treat date as local time zone
          const startDateToSet = parseISO(option.seasonStartDate);
          const endDateToSet = parseISO(option.seasonEndDate);
          return {
            label: `${format(startDateToSet, 'yyyy')} - ${format(
              endDateToSet,
              'yyyy'
            )} (${format(startDateToSet, 'MMMM d yyyy')} - ${format(
              endDateToSet,
              'MMMM d yyyy'
            )})`,
            value: [option.seasonStartDate, option.seasonEndDate].join('|'),
          };
        }
      });
    default:
      return [];
  }
}

export const CREATE_OR_UPDATE_FORM_NGB_INTEGRATION_MUTATION = gql`
  mutation CreateOrUpdateFormNgbIntegrations(
    $formId: Int!
    $ngbIntegrations: NgbIntegrationInput
  ) {
    createOrUpdateFormNgbIntegrations(
      formId: $formId
      ngbIntegrations: $ngbIntegrations
    ) {
      formId
      enabled
      ngbIntegrationId
    }
  }
`;

type MutationPayload = {
  formId: number;
  ngbIntegrations: {
    ngbIntegrationId: number;
    enabled: boolean;
    seasonEndDate?: string;
    seasonStartDate?: string;
  };
};

// NOTE: These logos should come from the BE
const LOGO_PER_NGB: {
  [key in NgbIntegration['code']]: string;
} = {
  usa_lacrosse: '/images/usa-lacrosse-logo.svg',
  usa_hockey: '/images/usa-hockey-logo.svg',
  hockey_canada: '/images/hockey-canada-logo.png',
};

function humanizeIsoDate(isoDate: string): string {
  const date = new Date(isoDate);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth() returns 0-based months, so add 1
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function useNgbIntregrationDefaultAssignment() {
  const [createOrUpdateFormNgbIntegration] = useMutation(
    CREATE_OR_UPDATE_FORM_NGB_INTEGRATION_MUTATION
  );

  const performDefaultAssignment = async ({
    ngbIntegrationData,
    formId,
    seasonEndDate,
  }: {
    ngbIntegrationData: NgbIntegration;
    formId: number;
    seasonEndDate: string | undefined | null;
  }) => {
    const defaultDataToSave = {
      formId,
      ngbIntegrations: {
        ngbIntegrationId: ngbIntegrationData.id,
        enabled: true,
        seasonStartDate: '',
        seasonEndDate: '',
      },
    };
    if (ngbIntegrationData.code === 'usa_lacrosse') {
      const todayDate = new Date();
      const oneYearFromNow = new Date(
        todayDate.setFullYear(todayDate.getFullYear() + 1)
      );
      defaultDataToSave.ngbIntegrations.seasonEndDate = seasonEndDate
        ? new Date(seasonEndDate).toISOString()
        : oneYearFromNow.toISOString();
    } else if (ngbIntegrationData.code === 'usa_hockey') {
      const seasonDatesField =
        ngbIntegrationData.metadata.formRequirements.fields.find(
          (field) => field.name === 'season_dates'
        );
      const seasonDateFirstOption = seasonDatesField?.options[0];
      if (seasonDateFirstOption) {
        defaultDataToSave.ngbIntegrations.seasonStartDate = new Date(
          seasonDateFirstOption.seasonStartDate
        ).toISOString();
        defaultDataToSave.ngbIntegrations.seasonEndDate = new Date(
          seasonDateFirstOption.seasonEndDate
        ).toISOString();
      }
    }
    const response = await createOrUpdateFormNgbIntegration({
      variables: defaultDataToSave,
    });
    return response;
  };

  const disableSavedNgbIntegrationForForm = async ({
    savedNgbIntegrationData,
    formId,
  }: {
    savedNgbIntegrationData: {
      id: number;
      enabled: boolean;
      ngbIntegrationId: number;
      season_end_date: string;
      season_start_date: string;
    };
    formId: number;
  }) => {
    const defaultDataToSave = {
      formId,
      ngbIntegrations: {
        ngbIntegrationId: savedNgbIntegrationData.ngbIntegrationId,
        enabled: false,
        seasonStartDate: savedNgbIntegrationData.season_start_date,
        seasonEndDate: savedNgbIntegrationData.season_end_date,
      },
    };
    const response = await createOrUpdateFormNgbIntegration({
      variables: defaultDataToSave,
    });
    return response;
  };

  return { performDefaultAssignment, disableSavedNgbIntegrationForForm };
}
